/**
 * Frontend @mention parser (feat/multi-agent-in-chat).
 *
 * Mirror of the server-side parser in
 * `packages/api/src/services/mention-parser.service.ts` — duplicated
 * client-side so the composer's autocomplete + active-mention chips
 * can update without a round-trip.
 *
 * Keep this file in lockstep with the server parser. The behaviour
 * contract is the same:
 *
 *   - `@argus-code <message>` parses to mentions=['argus-code']
 *   - Mid-sentence @ is NOT a routing mention
 *   - Reserved words (@everyone, @channel, @here, @all) are literal
 *   - Email-like text (@example.com) is NOT a mention
 *   - Hard cap of 5 leading mentions
 *
 * Why not import from the API package directly? @argusai/api is a
 * server-only package with DB / Fastify deps that can't safely be
 * bundled into the web bundle. This 60-line parser is the lightest
 * possible duplication; both files cite this comment so future edits
 * stay in sync.
 */

const RESERVED_MENTIONS = new Set(['everyone', 'channel', 'here', 'all']);
const MAX_MENTIONS = 5;
const SLUG_CHAR = /^[a-zA-Z0-9_-]$/;

export interface MentionParseResult {
  mentions: string[];
  body: string;
  hasMentions: boolean;
}

export function parseMentions(input: string): MentionParseResult {
  if (typeof input !== 'string' || input.length === 0) {
    return { mentions: [], body: input ?? '', hasMentions: false };
  }

  const mentions: string[] = [];
  const seen = new Set<string>();
  let cursor = 0;
  let lastValidCursor = 0;

  while (cursor < input.length && /\s/.test(input[cursor]!)) cursor++;

  while (cursor < input.length && input[cursor] === '@') {
    const tokenStart = cursor;
    cursor++;

    if (cursor < input.length && input[cursor] === '@') {
      cursor = tokenStart;
      break;
    }

    const slugStart = cursor;
    while (cursor < input.length && SLUG_CHAR.test(input[cursor]!)) cursor++;
    const slug = input.slice(slugStart, cursor);

    if (slug.length === 0) {
      cursor = tokenStart;
      break;
    }

    if (
      cursor < input.length &&
      /[,;:.!?]/.test(input[cursor]!) &&
      (cursor + 1 >= input.length || /\s/.test(input[cursor + 1]!))
    ) {
      cursor++;
    }

    if (cursor < input.length && !/\s/.test(input[cursor]!)) {
      cursor = tokenStart;
      break;
    }

    const lower = slug.toLowerCase();
    if (RESERVED_MENTIONS.has(lower)) {
      cursor = tokenStart;
      break;
    }

    if (!seen.has(lower)) {
      seen.add(lower);
      mentions.push(lower);
    }

    while (cursor < input.length && /\s/.test(input[cursor]!)) cursor++;
    lastValidCursor = cursor;

    if (mentions.length >= MAX_MENTIONS) break;
  }

  if (mentions.length === 0) {
    return { mentions: [], body: input, hasMentions: false };
  }

  const body = input.slice(lastValidCursor).replace(/^\s+/, '');
  return { mentions, body, hasMentions: true };
}

/**
 * Detect whether the textarea cursor is mid-mention (i.e. the user
 * just typed `@` followed by 0+ slug characters at the start of the
 * input OR after whitespace, and hasn't yet hit a non-slug character).
 *
 * Used by the composer to decide when to open the autocomplete
 * dropdown. Returns the partial slug typed so far (after the `@`) or
 * `null` when the cursor is not in mention position.
 *
 * Examples:
 *   - `@arg`        @ cursor=4 → 'arg'
 *   - `@argus-code` @ cursor=11 → 'argus-code'
 *   - `@argus-code ` cursor=12 → null (whitespace ended the mention)
 *   - `hi @`        cursor=4 → '' (just after @, no slug yet)
 *   - `hi @arg`     cursor=7 → 'arg' (mid-message mention IS allowed
 *                                     to trigger the dropdown; whether
 *                                     it becomes a routing mention is
 *                                     a separate check)
 */
export function detectMentionInProgress(
  text: string,
  cursorPos: number,
): { partial: string; mentionStart: number } | null {
  if (cursorPos < 0 || cursorPos > text.length) return null;
  // Walk back from cursor until we hit whitespace, '@', or beginning.
  let start = cursorPos;
  while (start > 0) {
    const ch = text[start - 1]!;
    if (ch === '@') {
      // Found a candidate mention.
      const before = start - 2 >= 0 ? text[start - 2] : null;
      // Mention is only valid when preceded by start-of-string or
      // whitespace. `hi@foo` is NOT a mention.
      if (before !== null && !/\s/.test(before)) return null;
      const partial = text.slice(start, cursorPos);
      // Reject if the partial contains any non-slug character (we
      // already left the mention).
      if (partial.length > 0 && !/^[a-zA-Z0-9_-]+$/.test(partial)) return null;
      return { partial, mentionStart: start - 1 };
    }
    if (/\s/.test(ch)) return null;
    start--;
  }
  return null;
}
